import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Role, UpsertRole } from "./types";

export const useRolesApi = () => {
  const api = useApi();

  const useAdminRoles = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/roles", query);
      return useSWR<Paginated<Role>, CustomError, string>(url, (url) => api.get<Paginated<Role>>({ url, signal }));
    },
    [api],
  );

  const getAdminRole = useCallback(
    (roleId: string, signal: AbortSignal) => {
      const url = `/admin/roles/${roleId}`;
      return useSWRMutation<Role | null, CustomError, string>(url, (url) => api.get<Role>({ url, signal }));
    },
    [api],
  );

  const insertAdminRole = useCallback(
    async (body: UpsertRole): Promise<{ id: string }> => {
      const url = `/admin/roles`;
      const id = await api.post<UpsertRole, { id: string }>(url, body);
      api.mutateCache([url, `/admin/users`]);
      return id;
    },
    [api],
  );

  const updateAdminRole = useCallback(
    async (roleId: string, body: UpsertRole): Promise<void> => {
      const url = `/admin/roles/${roleId}`;
      (await api.put<UpsertRole>(url, body), api.mutateCache([`/admin/roles`, `/admin/users`]));
    },
    [api],
  );

  const deleteAdminRole = useCallback(
    async (roleId: string): Promise<void> => {
      const url = `/admin/roles/${roleId}`;
      await api.del(url).then(() => {
        api.mutateCache(["/admin/roles"]);
      });
    },
    [api],
  );

  return {
    useAdminRoles,
    getAdminRole,
    insertAdminRole,
    updateAdminRole,
    deleteAdminRole,
  };
};
